import type { TrackReference, TrackReferenceOrPlaceholder } from '@livekit/components-react';
import { Track } from 'livekit-client';
import { livekitIdentityMatchesPlayerId } from '@/helpers/livekit-identity.helper';

/** True when this publication should drive a video tile (includes Unknown-source media-agent video). */
export function publicationIsVisual(trackRef: TrackReferenceOrPlaceholder): boolean {
  const pub = trackRef.publication;
  if (!pub) return false;
  if (pub.kind === Track.Kind.Video) return true;
  if (pub.kind === Track.Kind.Audio) return false;
  return trackRef.source !== Track.Source.Microphone && trackRef.source !== Track.Source.ScreenShareAudio;
}

/**
 * Filter visual tracks for remote participants (with authorization).
 * Mirrors the event-player subscriber path so preview and players resolve the same publications.
 */
export function filterSubscriberVisualTracks(
  tracks: TrackReferenceOrPlaceholder[],
  authorizedTrackSids: string[],
): TrackReference[] {
  return tracks.filter(
    (trackRef): trackRef is TrackReference =>
      trackRef.publication?.trackSid !== undefined &&
      !trackRef.participant.isLocal &&
      publicationIsVisual(trackRef) &&
      authorizedTrackSids.includes(trackRef.publication.trackSid),
  );
}

/**
 * Finds a visual track by publication SID in raw room tracks (no allowlist filter).
 * Use when an allowlisted SID is missing from {@link filterSubscriberVisualTracks} output due to timing.
 */
export function findVisualTrackByPublicationSid(
  tracks: TrackReferenceOrPlaceholder[],
  publicationSid: string,
): TrackReference | undefined {
  return tracks.find(
    (trackRef): trackRef is TrackReference =>
      trackRef.publication?.trackSid === publicationSid && publicationIsVisual(trackRef),
  );
}

/** LiveKit itemIds prefix → Mongo player id (e.g. `mediaAgent:{shellId}`). */
export function publisherPlayerIdFromLayerItemId(itemId?: string): string | undefined {
  if (!itemId) {
    return undefined;
  }
  const colonIdx = itemId.indexOf(':');
  if (colonIdx <= 0) {
    return undefined;
  }
  const prefix = itemId.slice(0, colonIdx);
  if (prefix === 'mediaAgent' || prefix === 'roomCam' || prefix === 'remoteSpeaker' || prefix === 'screenshare') {
    return itemId.slice(colonIdx + 1);
  }
  return undefined;
}

/**
 * Resolve a visual track from the publisher participant when layer track SIDs are missing or stale
 * (common right after reload before the next live-context recompute).
 */
export function findVisualTrackForPublisherPlayerId(
  tracks: TrackReferenceOrPlaceholder[],
  playerId: string,
  componentId?: string,
): TrackReference | undefined {
  const publisherTracks = tracks.filter(
    (trackRef): trackRef is TrackReference =>
      trackRef.publication?.trackSid !== undefined &&
      !trackRef.participant.isLocal &&
      publicationIsVisual(trackRef) &&
      livekitIdentityMatchesPlayerId(trackRef.participant.identity, playerId),
  );
  if (publisherTracks.length === 0) {
    return undefined;
  }
  if (componentId === 'livekitTrackRoomCam' || componentId === 'livekitTrackRemoteSpeaker') {
    return (
      publisherTracks.find(
        (t) => t.source === Track.Source.Camera && (t.publication?.trackName?.includes(':cropped') ?? false),
      ) ??
      publisherTracks.find((t) => t.source === Track.Source.Camera) ??
      publisherTracks[0]
    );
  }
  if (componentId === 'livekitTrackScreenshare') {
    return publisherTracks.find((t) => t.source === Track.Source.ScreenShare) ?? publisherTracks[0];
  }
  return publisherTracks[0];
}

/** Filter audio tracks; matches on publication.kind so Unknown-source agent audio is included. */
export function filterSubscriberAudioTracks(
  tracks: TrackReferenceOrPlaceholder[],
  authorizedTrackSids: string[],
): TrackReference[] {
  return tracks.filter(
    (trackRef): trackRef is TrackReference =>
      trackRef.publication?.trackSid !== undefined &&
      !trackRef.participant.isLocal &&
      trackRef.publication.kind === Track.Kind.Audio &&
      authorizedTrackSids.includes(trackRef.publication.trackSid),
  );
}
